import { useState, useEffect } from 'react';
import {
  Mail,
  Send,
  CheckCircle,
  AlertCircle,
  RefreshCw,
  Settings,
  Loader2,
} from 'lucide-react';
import api from '../../services/api';

export default function AdminEmailSettings() {
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [testResult, setTestResult] = useState(null);
  const [emailConfig, setEmailConfig] = useState({
    provider: 'Resend',
    fromEmail: '',
    adminEmail: '',
    status: 'unknown',
  });

  useEffect(() => {
    loadEmailConfig();
  }, []);

  const loadEmailConfig = async () => {
    setLoading(true);
    try {
      const response = await api.get('/admin/email/config');
      setEmailConfig(response.data?.data || {
        provider: 'Resend',
        fromEmail: 'admin@pharmunity.io',
        adminEmail: 'admin@pharmunity.io',
        status: 'configured',
      });
    } catch (error) {
      console.error('Failed to load email config:', error);
      // Set defaults if API fails
      setEmailConfig({
        provider: 'Resend',
        fromEmail: 'admin@pharmunity.io',
        adminEmail: 'admin@pharmunity.io',
        status: 'configured',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSendTestEmail = async (e) => {
    e.preventDefault();
    setSending(true);
    setTestResult(null);

    try {
      const response = await api.post('/admin/email/test', { email: testEmail });
      setTestResult({
        success: true,
        message: response.data?.message || 'Test email sent successfully!',
      });
    } catch (error) {
      setTestResult({
        success: false,
        message: error.response?.data?.error || 'Failed to send test email',
      });
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-red-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Email Settings</h1>
        <p className="text-gray-600">Configure email notifications and delivery</p>
      </div>

      {/* Email Configuration */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Email Configuration</h2>

        <div className="grid gap-6 md:grid-cols-2">
          {/* Provider Status */}
          <div className="p-4 rounded-lg border border-gray-200">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-medium text-gray-700">Email Provider</span>
              <span className={`flex items-center gap-1 text-sm ${
                emailConfig.status === 'configured' ? 'text-green-600' : 'text-yellow-600'
              }`}>
                {emailConfig.status === 'configured' ? (
                  <>
                    <CheckCircle className="w-4 h-4" />
                    Active
                  </>
                ) : (
                  <>
                    <AlertCircle className="w-4 h-4" />
                    {emailConfig.status}
                  </>
                )}
              </span>
            </div>
            <p className="text-2xl font-bold text-gray-900">{emailConfig.provider}</p>
            <p className="text-sm text-gray-500">Transactional email service</p>
          </div>

          {/* From Email */}
          <div className="p-4 rounded-lg border border-gray-200">
            <div className="flex items-center gap-2 mb-3">
              <Mail className="w-4 h-4 text-gray-500" />
              <span className="text-sm font-medium text-gray-700">From Address</span>
            </div>
            <p className="text-lg font-medium text-gray-900">{emailConfig.fromEmail}</p>
            <p className="text-sm text-gray-500">Sender email for all notifications</p>
          </div>

          {/* Admin Email */}
          <div className="p-4 rounded-lg border border-gray-200">
            <div className="flex items-center gap-2 mb-3">
              <Settings className="w-4 h-4 text-gray-500" />
              <span className="text-sm font-medium text-gray-700">Admin Notifications</span>
            </div>
            <p className="text-lg font-medium text-gray-900">{emailConfig.adminEmail}</p>
            <p className="text-sm text-gray-500">Receives system notifications</p>
          </div>

          {/* Domain Status */}
          <div className="p-4 rounded-lg border border-gray-200">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-medium text-gray-700">Domain</span>
              <span className="flex items-center gap-1 text-sm text-green-600">
                <CheckCircle className="w-4 h-4" />
                Verified
              </span>
            </div>
            <p className="text-lg font-medium text-gray-900">pharmunity.io</p>
            <p className="text-sm text-gray-500">DNS records configured</p>
          </div>
        </div>
      </div>

      {/* Test Email */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Send Test Email</h2>
        <p className="text-gray-600 mb-4">
          Send a test email to verify your email configuration is working correctly.
        </p>

        <form onSubmit={handleSendTestEmail} className="space-y-4">
          <div className="flex gap-4">
            <div className="flex-1">
              <input
                type="email"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                placeholder="Enter email address to test"
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
              />
            </div>
            <button
              type="submit"
              disabled={sending || !testEmail}
              className="flex items-center gap-2 px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
            >
              {sending ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  Send Test
                </>
              )}
            </button>
          </div>

          {testResult && (
            <div className={`p-4 rounded-lg ${
              testResult.success
                ? 'bg-green-50 border border-green-200'
                : 'bg-red-50 border border-red-200'
            }`}>
              <div className="flex items-center gap-2">
                {testResult.success ? (
                  <CheckCircle className="w-5 h-5 text-green-600" />
                ) : (
                  <AlertCircle className="w-5 h-5 text-red-600" />
                )}
                <span className={testResult.success ? 'text-green-700' : 'text-red-700'}>
                  {testResult.message}
                </span>
              </div>
            </div>
          )}
        </form>
      </div>

      {/* Email Templates Info */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Email Notifications</h2>
        <p className="text-gray-600 mb-4">
          The following automated emails are sent by the system:
        </p>

        <div className="space-y-3">
          <NotificationItem
            title="Introduction Request"
            description="Sent to admin when a new introduction request is submitted"
            recipient="Admin"
          />
          <NotificationItem
            title="Request Accepted"
            description="Sent to requester when their introduction request is approved"
            recipient="Requester"
          />
          <NotificationItem
            title="Request Declined"
            description="Sent to requester when their introduction request is declined"
            recipient="Requester"
          />
          <NotificationItem
            title="Introduction Completed"
            description="Sent to requester with target contact details after successful introduction"
            recipient="Requester"
          />
          <NotificationItem
            title="Password Reset"
            description="Sent when a user requests to reset their password"
            recipient="User"
          />
        </div>
      </div>
    </div>
  );
}

function NotificationItem({ title, description, recipient }) {
  return (
    <div className="flex items-start gap-3 p-3 rounded-lg border border-gray-100 hover:bg-gray-50">
      <div className="w-8 h-8 rounded-lg bg-red-100 flex items-center justify-center flex-shrink-0">
        <Mail className="w-4 h-4 text-red-600" />
      </div>
      <div className="flex-1">
        <p className="font-medium text-gray-900">{title}</p>
        <p className="text-sm text-gray-500">{description}</p>
      </div>
      <span className="text-xs px-2 py-1 bg-gray-100 text-gray-600 rounded-full">
        {recipient}
      </span>
    </div>
  );
}
